// Xinu operating-systems page: documentation-style notes grounded in the
// xinu-os/xinu source tree, with one static diagram per section.
import { useEffect } from 'react';
import '../../css/XinuDocs.css';
import ContactSection from '../../Components/ContactSection';
import courseworkProjects from '../../data/courseworkProjects';
import { applyPageSeo, resetSeo } from '../../seo';
import {
    BootDiagram,
    ThreadStateDiagram,
    MessageDiagram,
    ReadyQueueDiagram,
    MemoryDiagram,
} from './XinuDiagrams';

const intro = courseworkProjects['/coursework/operating-systems'].paragraphs;

const SECTIONS = [
    ['boot', 'Booting the kernel'],
    ['threads', 'Threads and the thread table'],
    ['messages', 'Message passing'],
    ['scheduling', 'Scheduling'],
    ['memory', 'Memory allocation'],
];

function Figure({ id, diagram, caption }) {
    return (
        <figure className="xinu-docs-figure" id={`${id}-diagram`}>
            {diagram}
            <figcaption>{caption}</figcaption>
        </figure>
    );
}

export default function XinuPage() {
    useEffect(() => {
        const previousTitle = document.title;
        document.title = 'Xinu Operating Systems · Aditya Patel';
        applyPageSeo('operating-systems');
        return () => { document.title = previousTitle; resetSeo(); };
    }, []);

    return (
        <div className="xinu-docs">
            <div className="xinu-docs-body">
                <p className="xinu-docs-crumb"><a href="/">Aditya Patel</a> / Xinu / CS 354</p>
                <h1>Xinu operating system notes</h1>
                {intro.map(({ id, content }) => <p className="xinu-docs-lede" key={id}>{content}</p>)}
                <p className="xinu-docs-meta">
                    Written from the labs I did in CS 354. Function and file names refer to the public
                    xinu-os/xinu source tree; my own lab solutions stay private.
                </p>

                <nav className="xinu-docs-toc" aria-label="Table of contents">
                    <p>Contents</p>
                    <ol>
                        {SECTIONS.map(([id, title]) => (
                            <li key={id}><a href={`#${id}`}>{title}</a></li>
                        ))}
                    </ol>
                </nav>

                <section className="xinu-docs-section" id="boot" aria-labelledby="boot-heading">
                    <h2 id="boot-heading">Booting the kernel</h2>
                    <p>
                        The system starts in assembly (<code>startup.S</code>), which sets up the C
                        environment and records where the heap begins. Control then reaches{' '}
                        <code>nulluser()</code> in <code>system/initialize.c</code>, the function
                        that builds the entire kernel before becoming the null thread.
                    </p>
                    <p>
                        <code>nulluser()</code> runs <code>platforminit()</code> for board-specific
                        setup, then <code>sysinit()</code>, which zeroes the thread table, builds
                        the free-memory list from the heap bounds, and initializes the null thread&apos;s
                        own entry. Only then are interrupts enabled and the first real thread created:{' '}
                        <code>ready(create(main, INITSTK, INITPRIO, &quot;MAIN&quot;, 0), RESCHED_YES)</code>.
                    </p>
                    <p>
                        After that, the null thread has nothing left to do but must never exit — it
                        spins in a <code>pause()</code> loop forever, always ready to run when nothing
                        else is.
                    </p>
                    <Figure
                        id="boot"
                        diagram={<BootDiagram />}
                        caption="Boot order in initialize.c: platform setup, kernel structures, interrupts, then the first thread."
                    />
                </section>

                <section className="xinu-docs-section" id="threads" aria-labelledby="threads-heading">
                    <h2 id="threads-heading">Threads and the thread table</h2>
                    <p>
                        Every thread in Xinu is a row in <code>thrtab</code>, an array of{' '}
                        <code>struct thrent</code> control blocks declared in{' '}
                        <code>system/initialize.c</code>. Each entry holds the thread&apos;s state,
                        priority, stack pointers, name, parent, message slot, and file descriptors.
                        States like <code>THRCURR</code>, <code>THRREADY</code>,{' '}
                        <code>THRSUSP</code>, and <code>THRRECV</code> are small integer constants
                        in <code>include/thread.h</code>.
                    </p>
                    <p>
                        <code>create()</code> in <code>system/create.c</code> allocates a stack with{' '}
                        <code>stkget()</code>, claims a free table slot with <code>thrnew()</code>,
                        fills in the control block, and leaves the new thread suspended —{' '}
                        <code>THRSUSP</code>. A separate <code>ready()</code> call moves it onto the
                        ready list. Interrupts are disabled across the whole sequence so the table
                        stays stable while it is being edited.
                    </p>
                    <Figure
                        id="threads"
                        diagram={<ThreadStateDiagram />}
                        caption="Thread states from include/thread.h and the calls that move between them."
                    />
                </section>

                <section className="xinu-docs-section" id="messages" aria-labelledby="messages-heading">
                    <h2 id="messages-heading">Message passing</h2>
                    <p>
                        Xinu threads communicate through a one-slot mailbox built into each thread
                        table entry: a <code>msg</code> field plus a <code>hasmsg</code> flag. There
                        is no queue — a second message to a thread that already holds one fails
                        with <code>SYSERR</code>.
                    </p>
                    <p>
                        <code>send()</code> in <code>system/send.c</code> validates the target with{' '}
                        <code>isbadtid()</code>, deposits the message, raises the flag, and — if the
                        receiver was blocked in <code>THRRECV</code> — wakes it with{' '}
                        <code>ready()</code>. <code>receive()</code> in{' '}
                        <code>system/receive.c</code> is the mirror image: if no message is waiting,
                        the thread marks itself <code>THRRECV</code> and calls{' '}
                        <code>resched()</code>; otherwise it copies the message out and clears the flag.
                    </p>
                    <Figure
                        id="messages"
                        diagram={<MessageDiagram />}
                        caption="Delivery is a store plus a flag; the flag falls only after the copy."
                    />
                </section>

                <section className="xinu-docs-section" id="scheduling" aria-labelledby="scheduling-heading">
                    <h2 id="scheduling-heading">Scheduling</h2>
                    <p>
                        Ready threads live on <code>readylist</code>, a queue ordered by priority.
                        The scheduler in <code>system/resched.c</code> is short: if the running
                        thread still outranks the head of the queue it keeps the CPU, otherwise the
                        current thread is reinserted by priority and the head is dequeued and entered
                        with a context switch (<code>ctxsw()</code>).
                    </p>
                    <p>
                        Preemption comes from the clock. <code>clkhandler()</code> in{' '}
                        <code>system/clkhandler.c</code> fires 1000 times per second
                        (<code>CLKTICKS_PER_SEC</code>), counts ticks up into the global{' '}
                        <code>clktime</code> seconds counter, wakes expired sleepers via{' '}
                        <code>wakeup()</code>, and otherwise calls <code>resched()</code> — so the
                        highest-priority ready thread is reselected on nearly every tick.
                    </p>
                    <Figure
                        id="scheduling"
                        diagram={<ReadyQueueDiagram />}
                        caption="The ready list stays priority-ordered; resched() picks the head."
                    />
                </section>

                <section className="xinu-docs-section" id="memory" aria-labelledby="memory-heading">
                    <h2 id="memory-heading">Memory allocation</h2>
                    <p>
                        The heap is a sorted linked list of free blocks rooted at{' '}
                        <code>memlist</code>, built by <code>sysinit()</code> from the heap bounds
                        the bootloader reported. Every request is rounded up to a multiple of the
                        block header size (<code>roundmb</code>).
                    </p>
                    <p>
                        <code>memget()</code> in <code>system/memget.c</code> walks the list for the
                        first block that fits: an exact fit is unlinked, a larger block is split with
                        the remainder left in place. <code>memfree()</code> in{' '}
                        <code>system/memfree.c</code> inserts the returned block back in address order
                        and merges it with adjacent free blocks so the list does not fragment.
                    </p>
                    <Figure
                        id="memory"
                        diagram={<MemoryDiagram />}
                        caption="The free list in address order; memget() splits, memfree() merges."
                    />
                </section>

                <div className="xinu-docs-seealso">
                    These notes are a static writeup. For the interactive treatment — guided traces,
                    a deterministic simulator, and a counterexample searcher — see the{' '}
                    <a href="/coursework/distributed-systems">Distributed Systems Labs</a> page.
                </div>

                <footer className="xinu-docs-footer">
                    <span>Private coursework · Solution code is not distributed</span>
                    <a href="https://github.com/xinu-os/xinu" target="_blank" rel="noreferrer">Explore the public Xinu project ↗</a>
                    <a href="https://xinu.cs.purdue.edu/" target="_blank" rel="noreferrer">Read about Xinu at Purdue ↗</a>
                    <a href="mailto:adityapatel1204+wrk@gmail.com?subject=Request%3A%20Xinu%20CS%20354%20notes%20and%20source%20code">Request notes &amp; source code →</a>
                    <a href="/">Back to portfolio →</a>
                </footer>
            </div>
            <ContactSection />
        </div>
    );
}
